import { Component, EventEmitter, Input, Output } from "@angular/core";
import { avatarColor, initialsOf, MeetingPerson } from "../meeting-room/meeting-utils";

@Component({
  selector: "app-meeting-people",
  standalone: false,
  templateUrl: "./meeting-people.component.html",
  styleUrls: ["./meeting-people.component.scss"],
})
export class MeetingPeopleComponent {
  @Input() people: MeetingPerson[] = [];
  @Input() names = new Map<string, string>();
  @Input() localName = "";
  @Input() pinnedId: string | null = null;
  @Input() canMute = true;
  /** Profile photo URLs keyed like `names` (external user id). Anyone without one, or whose photo fails to load, shows initials. */
  @Input() photos = new Map<string, string>();
  @Output() pin = new EventEmitter<string | null>();
  @Output() mute = new EventEmitter<string>();
  @Output() muteAll = new EventEmitter<void>();
  @Output() copyLink = new EventEmitter<void>();

  query = "";
  private brokenPhotos = new Set<string>();

  get raised(): MeetingPerson[] {
    return this.filtered
      .filter((person) => person.handRaisedAt != null)
      .sort((a, b) => (a.handRaisedAt || 0) - (b.handRaisedAt || 0));
  }

  get filtered(): MeetingPerson[] {
    const query = this.query.trim().toLowerCase();
    if (!query) return this.people;
    return this.people.filter((person) => this.nameOf(person).toLowerCase().includes(query));
  }

  get othersUnmuted(): boolean {
    return this.people.some((person) => !person.local && person.muted === false);
  }

  nameOf(person: MeetingPerson): string {
    if (person.local) return this.localName || "You";
    return this.names.get(person.externalUserId) || "Participant";
  }

  initials(person: MeetingPerson): string {
    return initialsOf(this.nameOf(person));
  }

  color(person: MeetingPerson) {
    return avatarColor(person.externalUserId || person.attendeeId);
  }

  photoOf(person: MeetingPerson): string {
    const url = this.photos?.get(person.externalUserId) || "";
    return url && !this.brokenPhotos.has(url) ? url : "";
  }

  photoFailed(url: string) {
    this.brokenPhotos.add(url);
  }

  hasVideo(person: MeetingPerson): boolean {
    return person.tileId != null && !person.paused;
  }

  signalLabel(person: MeetingPerson): string {
    if (person.signal == null) return "";
    if (person.signal >= 1) return "Good connection";
    if (person.signal >= 0.5) return "Fair connection";
    return "Poor connection";
  }

  trackPerson(_: number, person: MeetingPerson) {
    return person.attendeeId;
  }
}
